import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '../../common-service';
import { GetAllServicoTomadoResponse } from '../../responses/repo-dfse/get-all-servico-tomado.response';
import { ServicoTomadoListaRequest } from '../../requests/repo-dfse/servico-tomado-lista.request';
import { ServicoTomadoGetByIdResponse } from '../../responses/repo-dfse/servico-tomado-get-by-id.response';
import { ServicoTomadoCreateResponse } from '../../responses/repo-dfse/servico-tomado-create.response';
import { ServicoTomadoCreateRequest } from '../../requests/repo-dfse/servico-tomado-create.request';
import { AtualizarTagsNFSeEntradaRequest } from '../../requests/repo-dfse/atualizar-tags-nfse-entrada.request';
import { NFSeEntradaTagsResponse } from '../../responses/repo-dfse/nfse-entrada-tags.response';
import { NFSeEntradaHistoricoListarResponse } from '../../responses/repo-dfse/nfse-entrada-historico-listar.response';
import { NFSeEntradaOcorrenciaCriarRequest } from '../../requests/repo-dfse/nfse-entrada-ocorrencia-criar.request';
import { ReturnStruct } from '../../structs/return.struct';

@Injectable({
  providedIn: 'root',
})
export class ServicoTomadoService extends CommonService {
  private readonly apiUrl: string = environment.urlApiRepoDFSe + 'NFSeEntrada';

  constructor(private httpClient: HttpClient) {
    super();
  }

  /**
   * Obtém a listagem paginada de Serviços Tomados.
   * Corresponde a: [HttpPost("getAll")]
   */
  public getAll(request: ServicoTomadoListaRequest, paginaAtual: number = 0): Observable<GetAllServicoTomadoResponse> {
    const url = `${this.apiUrl}/getAll`;
    const params = new HttpParams().set('paginaAtual', paginaAtual.toString());

    return this.httpClient
      .post<GetAllServicoTomadoResponse>(url, request, {
        params,
        ...this.addHeaderToken()
      })
      .pipe(catchError(this.handleError));
  }

  /**
   * Obtém o detalhe completo de um Serviço Tomado pelo ID.
   * Corresponde a: [HttpGet("idNFSeEntrada/{idNFSeEntrada}")]
   */
  public getById(idNFSeEntrada: number): Observable<ServicoTomadoGetByIdResponse> {
    const url = `${this.apiUrl}/idNFSeEntrada/${idNFSeEntrada}`;
    return this.httpClient
      .get<ServicoTomadoGetByIdResponse>(url, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Cria um novo Serviço Tomado.
   * Corresponde a: [HttpPost]
   */
  public create(request: ServicoTomadoCreateRequest): Observable<ServicoTomadoCreateResponse> {
    return this.httpClient
      .post<ServicoTomadoCreateResponse>(this.apiUrl, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Atualiza um Serviço Tomado existente.
   * Corresponde a: [HttpPut("idNFSeEntrada/{idNFSeEntrada}")]
   */
  public update(idNFSeEntrada: number, request: ServicoTomadoCreateRequest): Observable<ReturnStruct> {
    const url = `${this.apiUrl}/idNFSeEntrada/${idNFSeEntrada}`;
    return this.httpClient
      .put<ReturnStruct>(url, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Obtém as tags atualmente vinculadas a um Serviço Tomado.
   * Corresponde a: [HttpGet("idNFSeEntrada/{idNFSeEntrada}/tags")]
   */
  public getTags(idNFSeEntrada: number): Observable<NFSeEntradaTagsResponse> {
    const url = `${this.apiUrl}/idNFSeEntrada/${idNFSeEntrada}/tags`;
    return this.httpClient
      .get<NFSeEntradaTagsResponse>(url, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Substitui o conjunto de tags vinculadas a um Serviço Tomado.
   * Corresponde a: [HttpPut("idNFSeEntrada/{idNFSeEntrada}/tags")]
   */
  public updateTags(idNFSeEntrada: number, idsTagServicoTomado: number[]): Observable<ReturnStruct> {
    const url = `${this.apiUrl}/idNFSeEntrada/${idNFSeEntrada}/tags`;
    const request: AtualizarTagsNFSeEntradaRequest = { idsTagServicoTomado };
    return this.httpClient
      .put<ReturnStruct>(url, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Histórico do Serviço Tomado: todas as ocorrências, pela data do fato, mais recentes primeiro.
   * Corresponde a: [HttpGet("idNFSeEntrada/{idNFSeEntrada}/historico")]
   */
  public getHistorico(idNFSeEntrada: number): Observable<NFSeEntradaHistoricoListarResponse> {
    const url = `${this.apiUrl}/idNFSeEntrada/${idNFSeEntrada}/historico`;
    return this.httpClient
      .get<NFSeEntradaHistoricoListarResponse>(url, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /**
   * Grava a anotação do usuário no histórico do Serviço Tomado (tipo Anotação, origem Usuário).
   * Corresponde a: [HttpPost("idNFSeEntrada/{idNFSeEntrada}/ocorrencia")]
   */
  public criarOcorrencia(idNFSeEntrada: number, request: NFSeEntradaOcorrenciaCriarRequest): Observable<ReturnStruct> {
    const url = `${this.apiUrl}/idNFSeEntrada/${idNFSeEntrada}/ocorrencia`;
    return this.httpClient
      .post<ReturnStruct>(url, request, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }
}
